import React, { createRef, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";

import { ReactComponent as IconCheckOn } from "../../../../../assets/images/AnswerList/icon_check_on.svg";
import { ReactComponent as IconCheckOff } from "../../../../../assets/images/AnswerList/icon_check_off.svg";
import { ReactComponent as IconArrow } from "../../../../../assets/images/AnswerList/icon_arrow.svg";
import {
  AnswersByStudent,
  IClass,
  OnePagePortfolioAssessmentAnswersByClass,
  OnePagePortfolioAssessmentQuestionAnswers,
} from "../../../../../state";
import { generateFullName } from "../../../../../libs/Util";
import DropdownMenu, { Option } from "../../../../../atoms/DropdownMenu";
import SwitchBox from "../../../AnswerList/SwitchBox";
import AnswerBox from "../../AnswerBox";

import styles from "./ClassAnswers.module.scss";

const SORT_TYPE = {
  NewestAnswer: 1,
  ContentLength: 2,
  StudentNumber: 3,
};

const OPTIONS: Option[] = [
  { label: "回答の新しい順", value: SORT_TYPE.NewestAnswer },
  { label: "文字数の多い順", value: SORT_TYPE.ContentLength },
  { label: "学籍番号順", value: SORT_TYPE.StudentNumber },
];

const DEFAULT_VALUE = OPTIONS[0];

type StudentListProps = {
  clId: number;
  answersByClass: OnePagePortfolioAssessmentAnswersByClass;
  onClickStudent: (student_id: number) => void;
};

const StudentList = ({
  clId,
  answersByClass,
  onClickStudent,
}: StudentListProps) => {
  const answers = answersByClass?.answers
    .flatMap(answer => ({
      student: answer.student,
      index: answer.index,
      haveAnswers: answer.have_answers,
      student_answers:
        answer.one_page_portfolio_assessments_question_answers?.flatMap(
          q => q.one_page_portfolio_assessment_answers,
        ) ?? [],
    }))
    .sort((a, b) => {
      return a.student.last_name_kana.localeCompare(
        b.student.last_name_kana,
        "ja",
      );
    });

  const renderStudentList = () => {
    if (answersByClass?.name_opened) {
      return answers.map(answer => (
        <div
          className={styles.studentItem}
          key={answer.student.id}
          data-sn-tooltip={`${generateFullName([
            answer.student.last_name,
            answer.student.first_name,
          ])}\n${generateFullName([
            answer.student.last_name_kana,
            answer.student.first_name_kana,
          ])}`}
          onClick={() => onClickStudent(answer.student.id)}
        >
          <div className={styles.studentContent}>
            {answer.haveAnswers ? (
              <IconCheckOn className={styles.icon} />
            ) : (
              <IconCheckOff className={styles.icon} />
            )}
            <div className={styles.fullName}>
              <span className={styles.name}>
                {generateFullName([
                  answer.student.last_name,
                  answer.student.first_name,
                ])}
              </span>
              <span className={styles.kana}>
                {generateFullName([
                  answer.student.last_name_kana,
                  answer.student.first_name_kana,
                ])}
              </span>
            </div>
          </div>
        </div>
      ));
    }

    if (answers.every(answer => answer.student_answers.length === 0))
      return <p className={styles.emptyMessage}>まだ回答がありません。</p>;

    return answers.map(
      a =>
        a.haveAnswers && (
          <div
            key={a.student.id}
            className={styles.studentItem}
            onClick={() => onClickStudent(a.student.id)}
          >
            <p className={styles.name}>回答{a.index}</p>
          </div>
        ),
    );
  };

  return (
    <div className="collapse show" id={`listStudent${clId}`}>
      <div className={styles.studentList}>{renderStudentList()}</div>
    </div>
  );
};

type ClassAnswersSummaryProps = {
  classList: number[];
  answersByClasses: OnePagePortfolioAssessmentAnswersByClass[];
  classes: IClass[];
  onChangeNameOpenClose: (class_id: number, opened: boolean) => void;
  onClickStudent: (student_id: number) => void;
};

const ClassAnswersSummary = ({
  classList,
  answersByClasses,
  classes,
  onChangeNameOpenClose,
  onClickStudent,
}: ClassAnswersSummaryProps) => {
  return (
    <div className={styles.classes}>
      {classList.map(clId => {
        const answersByClass = answersByClasses.find(k => k.id === clId);
        return (
          <div className={styles.controller} key={clId}>
            <div className={styles.head}>
              <button
                className={styles.toggleButton}
                data-bs-toggle="collapse"
                aria-expanded="true"
                data-bs-target={`#listStudent${clId}`}
              >
                <IconArrow className={styles.iconArrow} />
                <p className={styles.class}>
                  {classes.find(cls => cls.id === clId)?.full_name}
                </p>
              </button>
              {answersByClass && (
                <div className={styles.switchGroup}>
                  <SwitchBox
                    label="生徒名をクラスに公開"
                    type="name_opened"
                    answersByClass={answersByClass}
                    onChange={onChangeNameOpenClose}
                  />
                </div>
              )}
            </div>
            {answersByClass && (
              <StudentList
                clId={clId}
                answersByClass={answersByClass}
                onClickStudent={onClickStudent}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

type ClassAnswersProps = {
  answersByStudents: AnswersByStudent[];
  answersByClasses: OnePagePortfolioAssessmentAnswersByClass[];
  classList: number[];
  classes: IClass[];
  changeNameOpenClose: (class_id: number, opened: boolean) => void;
};

const ClassAnswers = ({
  answersByStudents,
  answersByClasses,
  classList,
  classes,
  changeNameOpenClose,
}: ClassAnswersProps) => {
  const [sortType, setSortType] = useState(DEFAULT_VALUE);
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const scrollRefs = useRef<any[]>([]);
  const highlightTimerRef = useRef<NodeJS.Timeout | null>(null);

  const hasClosedNameInAnswersByClasses = answersByClasses.some(
    answer => !answer.name_opened,
  );
  const availableSortOptions = hasClosedNameInAnswersByClasses
    ? OPTIONS.filter(option => option.value !== SORT_TYPE.StudentNumber)
    : OPTIONS;

  const handleChangeNameOpenClose = (class_id: number, opened: boolean) => {
    if (!opened && sortType.value === SORT_TYPE.StudentNumber) {
      setSortType(DEFAULT_VALUE);
    }
    changeNameOpenClose(class_id, opened);
  };

  const sortedAnswersByStudents = useMemo(() => {
    const getContentsLength = (
      questionAnswers: OnePagePortfolioAssessmentQuestionAnswers[],
    ) =>
      questionAnswers
        .flatMap(qa => qa.one_page_portfolio_assessment_answers)
        .flatMap(a => a.contents.length)
        .reduce((sum, v) => sum + v, 0);

    const getLatestTime = (
      questionAnswers: OnePagePortfolioAssessmentQuestionAnswers[],
    ) => {
      const times = questionAnswers
        .flatMap(qa => qa.one_page_portfolio_assessment_answers)
        .map(a => (a.updated_at ? Date.parse(a.updated_at) : 0));

      return Math.max(...times);
    };

    switch (sortType.value) {
      case SORT_TYPE.ContentLength:
        return [...answersByStudents].sort(
          (x, y) =>
            getContentsLength(y.questionAnswers) -
            getContentsLength(x.questionAnswers),
        );
      case SORT_TYPE.StudentNumber:
        return [...answersByStudents].sort((x, y) => {
          const collator = new Intl.Collator(["ja", "en"], { numeric: true });

          const a = x.student?.student_no_in_school ?? "";
          const b = y.student?.student_no_in_school ?? "";

          if (!a) return 1;
          if (!b) return -1;

          return collator.compare(a, b);
        });
      default:
        return [...answersByStudents].sort(
          (x, y) =>
            getLatestTime(y.questionAnswers) - getLatestTime(x.questionAnswers),
        );
    }
  }, [answersByStudents, sortType]);

  const scrollSmoothHandler = (index: number) => {
    scrollRefs.current[index]?.current?.scrollIntoView({ behavior: "smooth" });
  };

  sortedAnswersByStudents.forEach((_, i) => {
    scrollRefs.current[i] = createRef();
  });

  const handleClickStudent = (student_id: number) => {
    const index = sortedAnswersByStudents.findIndex(
      sortedAnswer => sortedAnswer.student?.id === student_id,
    );
    if (index < 0) return;

    scrollSmoothHandler(index);
    setHighlighted(index);
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = setTimeout(() => setHighlighted(null), 1500);
  };

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      <ClassAnswersSummary
        classList={classList}
        answersByClasses={answersByClasses}
        classes={classes}
        onChangeNameOpenClose={handleChangeNameOpenClose}
        onClickStudent={handleClickStudent}
      />
      <div className="d-flex align-items-center justify-content-end mt-4">
        <DropdownMenu
          options={availableSortOptions}
          defaultValue={sortType}
          onChange={value => {
            const selectSort = availableSortOptions.find(
              option => option.value === value,
            ) as Option;
            setSortType(selectSort);
          }}
        />
      </div>
      <div className={clsx(styles.answerList, "mt-4")}>
        {sortedAnswersByStudents.some(answer => answer.haveAnswers) ? (
          sortedAnswersByStudents.map(
            (sortedAnswer, index) =>
              sortedAnswer.haveAnswers && (
                <AnswerBox
                  key={`answer-${sortedAnswer.student?.id}`}
                  nameOpened={sortedAnswer.nameOpened}
                  classFullName={
                    classList.length > 1
                      ? sortedAnswer.classFullName
                      : undefined
                  }
                  index={sortedAnswer.index}
                  studentFullName={sortedAnswer.student?.full_name ?? ""}
                  studentNoInSchool={
                    sortedAnswer.student?.student_no_in_school ?? ""
                  }
                  studentAnswers={sortedAnswer.questionAnswers}
                  isHighlighted={highlighted === index}
                  ref={scrollRefs.current[index]}
                />
              ),
          )
        ) : (
          <p className={styles.emptyMessageBox}>まだ回答がありません。</p>
        )}
      </div>
    </>
  );
};

export default ClassAnswers;
